import React, { useState } from 'react';
import { Check, Download, ShieldCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import { usePermissions } from '../../hooks/usePermissions';
import type {
  CompanyDashboard, ControlEvent, DashboardTrend, DepartmentAggregate, GraduationProposal, OwnerAdoption,
  SessionException,
} from '../../types/aai';
import {
  Action, Chip, Divided, Drawer, EmptyState, ErrorNote, Kicker, ListRow, Loading, MiniBarChart, NotMeasured, PageBody,
  PageHead, Panel, PanelHead, RouteMark, Select, Stat,
} from '../../components/aa';
import type { TrendPoint } from '../../components/aa';
import { Tiles, sentLine } from './ControlDrawers';
import { relativeTime } from '../partner/format';

/*
 * Adoption controls, for the Owner.
 *
 * The Owner sponsors the adoption program and does not run it. This page reads
 * the program's current configuration, the sessions that cannot be delivered
 * as planned, and the graduation proposals waiting for a decision, and lets the
 * Owner review each one. A review is a recorded request to the program's
 * manager or the AI Champion; the one direct control is the program switch.
 *
 * Readiness stays on this page in compact form: aggregates only, never
 * individuals. Suppressed groups read as protected, not as zero.
 */

const MIN_GROUP_HINT = 'Groups under the minimum size are hidden to protect privacy';
const FIELD = 'w-full rounded-card border border-line-strong bg-white px-4 py-3 text-[13.5px] leading-[21px] text-ink outline-none placeholder:text-ink-soft focus:border-ink';
const LABEL = 'mb-2 block text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft';

const weekLabel = (week: string): string => {
  const at = new Date(week);
  if (Number.isNaN(at.getTime())) return week;
  return at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const dateLabel = (iso: string | null | undefined, fallback: string): string => {
  if (!iso) return fallback;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return fallback;
  return at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const trendPoints = (trend: DashboardTrend | null): TrendPoint[] => {
  if (!trend || trend.suppressed || trend.points.length < 2) return [];
  return trend.points.map((p) => ({ label: weekLabel(p.week), value: p.maturity_pct }));
};

const maturityValue = (pct: number | null | undefined, suppressed: boolean): React.ReactNode => {
  if (suppressed || pct === null || pct === undefined) return <NotMeasured reason="Protected" />;
  return `${pct}%`;
};

const names = (emails: string[]): string => (emails.length === 0 ? 'Not named yet' : emails.join(', '));

const useRecord = (onRecorded: () => void) => {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<ControlEvent | null>(null);
  const run = async (body: { kind: string; subject: string; status: string; payload: Record<string, unknown> }, message: string) => {
    setBusy(true);
    try {
      const res = await aaiApi.recordControlEvent(body);
      setDone(res.data);
      toast.success(`${message} ${sentLine(res.data)}`);
      onRecorded();
    } catch {
      toast.error('That did not record.');
    } finally {
      setBusy(false);
    }
  };
  return { busy, done, run };
};

/* ── Program review ─────────────────────────────────────────────────────── */

type ProgramAction = 'confirm' | 'request_change' | 'request_retirement' | 'pause' | 'resume';

const PROGRAM_ACTIONS: Record<ProgramAction, { label: string; status: string; done: string; needsReason: boolean }> = {
  confirm: { label: 'Confirm the current configuration', status: 'confirmed', done: 'Configuration confirmed.', needsReason: false },
  request_change: { label: 'Request a change to the configuration', status: 'change_requested', done: 'Change requested.', needsReason: true },
  request_retirement: { label: 'Request retirement of the program', status: 'retirement_requested', done: 'Retirement requested.', needsReason: true },
  pause: { label: 'Pause the program now', status: 'paused', done: 'Program paused.', needsReason: true },
  resume: { label: 'Resume the program now', status: 'resumed', done: 'Program resumed.', needsReason: false },
};

const ProgramReviewDrawer: React.FC<{ data: OwnerAdoption; companyName: string; canSwitch: boolean; onClose: () => void; onRecorded: () => void }> = ({
  data, companyName, canSwitch, onClose, onRecorded,
}) => {
  const { busy, done, run } = useRecord(onRecorded);
  const program = data.program!;
  const [action, setAction] = useState<ProgramAction>('confirm');
  const [reason, setReason] = useState('');
  const [switching, setSwitching] = useState(false);

  const options = (Object.keys(PROGRAM_ACTIONS) as ProgramAction[]).filter((key) => {
    if (key === 'pause') return canSwitch && program.enabled;
    if (key === 'resume') return canSwitch && !program.enabled;
    return true;
  });

  const submit = async () => {
    const spec = PROGRAM_ACTIONS[action];
    if (spec.needsReason && !reason.trim()) {
      toast.error('Say what changed in the work, policy, or evidence.');
      return;
    }
    if (action === 'pause' || action === 'resume') {
      setSwitching(true);
      try {
        await aaiApi.updateProgram({ enabled: action === 'resume' });
      } catch {
        toast.error('Could not switch the program.');
        setSwitching(false);
        return;
      }
      setSwitching(false);
    }
    await run({
      kind: 'program_review', subject: program.name, status: spec.status,
      payload: { action, note: reason.trim(), preset: program.preset, tier: program.tier, managed_by: program.managed_by },
    }, spec.done);
  };

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Program review</Kicker>}
      badge={done ? <Chip>Recorded</Chip> : <Chip tone={data.review_due ? 'warn' : 'ok'}>{data.review_due ? 'Review due' : 'Configuration current'}</Chip>}
      title={`Review ${program.name} for ${companyName}`}
      sub={done ? sentLine(done) : 'Review the current configuration, the manager, and the next review before asking for a change. Nothing changes live until the manager acts, except the program switch.'}
      footer={(
        <div className="flex flex-wrap items-center justify-end gap-3">
          {!done && <Action size="lg" variant="quiet" onClick={onClose}>Cancel</Action>}
          {!done && <Action size="lg" onClick={submit} disabled={busy || switching} reason="Recording">Record program action</Action>}
          {done && <Action size="lg" variant="quiet" onClick={onClose}>Close</Action>}
        </div>
      )}
    >
      <Tiles
        facts={[
          { label: 'Current configuration', value: `${program.preset || 'Custom'} · ${program.phases} phases · ${program.tier_label}` },
          { label: 'Manager', value: `${program.manager_label}: ${names(data.managers)}` },
          { label: 'Last updated', value: relativeTime(program.updated_at) },
          { label: 'Next review', value: data.review_due ? 'Due now' : dateLabel(data.next_review, 'Not scheduled') },
        ]}
      />
      {!done && (
        <>
          <label className="mt-5 block">
            <span className={LABEL}>Program action</span>
            <Select value={action} onChange={(e) => setAction(e.target.value as ProgramAction)}>
              {options.map((key) => <option key={key} value={key}>{PROGRAM_ACTIONS[key].label}</option>)}
            </Select>
          </label>
          <label className="mt-4 block">
            <span className={LABEL}>Reason{PROGRAM_ACTIONS[action].needsReason ? '' : ' (optional)'}</span>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} className={FIELD} placeholder="What changed in the work, policy, or evidence?" />
          </label>
          <p className="m-0 mt-4 text-[12.5px] leading-[20px] text-ink-dim">
            Confirmations and requests go to the program manager ({names(data.managers)}) and are recorded in Activity. Pausing stops new sessions and Quick Win runs for the program and is yours to do directly.
          </p>
        </>
      )}
    </Drawer>
  );
};

/* ── Session exception ──────────────────────────────────────────────────── */

type Resolution = 'assign_cohort' | 'reschedule' | 'cancel';

const RESOLUTIONS: Record<Resolution, string> = {
  assign_cohort: 'Assign an existing cohort',
  reschedule: 'Reschedule the session',
  cancel: 'Cancel the session',
};

const SessionExceptionDrawer: React.FC<{ item: SessionException; data: OwnerAdoption; onClose: () => void; onRecorded: () => void }> = ({
  item, data, onClose, onRecorded,
}) => {
  const { busy, done, run } = useRecord(onRecorded);
  const [resolution, setResolution] = useState<Resolution>(data.cohorts.length > 0 ? 'assign_cohort' : 'reschedule');
  const [cohortId, setCohortId] = useState(data.cohorts[0]?.id || '');
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const programName = data.program?.name || 'the adoption program';

  const submit = () => {
    if (resolution === 'assign_cohort' && !cohortId) {
      toast.error('Pick a cohort.');
      return;
    }
    if (resolution === 'reschedule' && !date) {
      toast.error('Pick a new date.');
      return;
    }
    const cohort = data.cohorts.find((c) => c.id === cohortId);
    return run({
      kind: 'session_exception', subject: item.id, status: resolution,
      payload: {
        title: item.title, exception: item.exception, resolution, cohort_id: cohort?.id, cohort: cohort?.name,
        new_date: date || undefined, note: note.trim(),
      },
    }, 'Resolution requested.');
  };

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Adoption management</Kicker>}
      badge={<Chip tone={done ? 'neutral' : 'warn'}>{done ? 'Requested' : `${data.exceptions.length} exception${data.exceptions.length === 1 ? '' : 's'}`}</Chip>}
      title={`Resolve: ${item.title}`}
      sub={done ? sentLine(done) : 'Give this session a cohort and a date the manager can deliver, or retire it.'}
      footer={(
        <div className="flex flex-wrap items-center justify-end gap-3">
          {!done && <Action size="lg" variant="quiet" onClick={onClose}>Cancel</Action>}
          {!done && <Action size="lg" onClick={submit} disabled={busy} reason="Sending">Send resolution</Action>}
          {done && <Action size="lg" variant="quiet" onClick={onClose}>Close</Action>}
        </div>
      )}
    >
      <Tiles
        facts={[
          { label: 'Cohort', value: item.cohort || 'None invited' },
          { label: 'Exception', value: item.exception },
          { label: 'Affected program', value: programName },
          { label: 'Scheduled', value: item.scheduled_at ? `${dateLabel(item.scheduled_at, 'Not set')} · ${item.session_type}` : 'Not set' },
        ]}
      />
      {!done && (
        <>
          <label className="mt-5 block">
            <span className={LABEL}>Resolution</span>
            <Select value={resolution} onChange={(e) => setResolution(e.target.value as Resolution)}>
              {(Object.keys(RESOLUTIONS) as Resolution[]).map((key) => (
                <option key={key} value={key} disabled={key === 'assign_cohort' && data.cohorts.length === 0}>
                  {RESOLUTIONS[key]}{key === 'assign_cohort' && data.cohorts.length === 0 ? ' (no cohorts yet)' : ''}
                </option>
              ))}
            </Select>
          </label>
          {resolution === 'assign_cohort' && (
            <label className="mt-4 block">
              <span className={LABEL}>Cohort</span>
              <Select value={cohortId} onChange={(e) => setCohortId(e.target.value)}>
                {data.cohorts.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.members} people</option>)}
              </Select>
            </label>
          )}
          {resolution === 'reschedule' && (
            <label className="mt-4 block">
              <span className={LABEL}>New date</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={FIELD} />
            </label>
          )}
          <label className="mt-4 block">
            <span className={LABEL}>Note (optional)</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={FIELD} placeholder="Anything the manager should know." />
          </label>
          <p className="m-0 mt-4 text-[12.5px] leading-[20px] text-ink-dim">
            Sessions are scheduled by the program manager ({names(data.managers)}). This sends them your resolution and records it; the session changes when they apply it.
          </p>
        </>
      )}
    </Drawer>
  );
};

/* ── Readiness evidence review ──────────────────────────────────────────── */

type EvidenceResult = 'confirmed' | 'needs_evidence';

const EvidenceReviewDrawer: React.FC<{ item: GraduationProposal; onClose: () => void; onRecorded: () => void }> = ({ item, onClose, onRecorded }) => {
  const { busy, done, run } = useRecord(onRecorded);
  const [result, setResult] = useState<EvidenceResult>('confirmed');
  const [note, setNote] = useState('');
  const rate = item.runs > 0 ? `${Math.round((item.worked / item.runs) * 100)}% worked` : 'No reviewed runs';

  const submit = () => {
    if (result === 'needs_evidence' && !note.trim()) {
      toast.error('Say what evidence is missing.');
      return;
    }
    return run({
      kind: 'evidence_review', subject: item.id, status: result,
      payload: { skill: item.skill, from_scope: item.from_scope, to_scope: item.to_scope, department: item.department, runs: item.runs, worked: item.worked, note: note.trim() },
    }, 'Evidence review recorded.');
  };

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Readiness evidence</Kicker>}
      badge={<Chip tone={done ? 'neutral' : 'warn'}>{done ? 'Reviewed' : 'Pending'}</Chip>}
      title={`Review evidence: ${item.skill}`}
      sub={done ? sentLine(done) : 'Confirm the definitions and the privacy threshold before the AI Champion decides on sharing this Quick Win.'}
      footer={(
        <div className="flex flex-wrap items-center justify-end gap-3">
          {!done && <Action size="lg" variant="quiet" onClick={onClose}>Cancel</Action>}
          {!done && <Action size="lg" onClick={submit} disabled={busy} reason="Recording">Record review</Action>}
          {done && <Action size="lg" variant="quiet" onClick={onClose}>Close</Action>}
        </div>
      )}
    >
      <Tiles
        facts={[
          { label: 'Proposed by', value: item.proposed_by || 'Not recorded' },
          { label: 'Scope change', value: `${item.from_scope} to ${item.to_scope}${item.department ? ` · ${item.department}` : ''}` },
          { label: 'Reviewed runs', value: `${item.runs} runs · ${rate}` },
          { label: 'Privacy threshold', value: '4 people · platform rule' },
        ]}
      />
      {item.note && (
        <div className="mt-5 rounded-card bg-canvas-inset px-5 py-4">
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-muted">Why it was proposed</div>
          <p className="m-0 text-[13.5px] leading-[21px] text-ink">{item.note}</p>
        </div>
      )}
      {!done && (
        <>
          <label className="mt-5 block">
            <span className={LABEL}>Review result</span>
            <Select value={result} onChange={(e) => setResult(e.target.value as EvidenceResult)}>
              <option value="confirmed">Definitions and privacy threshold confirmed</option>
              <option value="needs_evidence">More evidence needed before sharing</option>
            </Select>
          </label>
          <label className="mt-4 block">
            <span className={LABEL}>Note{result === 'needs_evidence' ? '' : ' (optional)'}</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={FIELD} placeholder="What the AI Champion should weigh." />
          </label>
          <p className="m-0 mt-4 text-[12.5px] leading-[20px] text-ink-dim">
            The AI Champion decides whether a personal Quick Win is shared with a team. Your review is recorded beside the proposal and sent to them.
          </p>
        </>
      )}
    </Drawer>
  );
};

/* ── Evidence rules ─────────────────────────────────────────────────────── */

// The rules the platform actually applies when it counts adoption evidence.
// Each line names a rule that exists in code or data; nothing aspirational.
const EVIDENCE_RULES = [
  { label: 'Meaningful use', value: '3 or more reviewed runs, at least one that worked' },
  { label: 'Sustained use', value: 'Re-measured weekly; a trend needs two or more weeks' },
  { label: 'Privacy threshold', value: '4 people · platform rule' },
  { label: 'Excluded', value: 'Prompts, practice answers, reflections, rankings' },
];

const WHAT_HAPPENS = [
  { title: 'Personal activity stays personal', desc: 'Only the participant sees their own prompts and practice responses.' },
  { title: 'Teams receive protected evidence', desc: 'Groups under the threshold are suppressed and no leaderboard exists.' },
  { title: 'Outcomes are reviewed by people', desc: 'Each run review records who reviewed it, when, and the outcome.' },
];

export const EvidenceRulesDrawer: React.FC<{ managers: string[]; managerLabel: string; onClose: () => void }> = ({ managers, managerLabel, onClose }) => (
  <Drawer
    onClose={onClose}
    kicker={<Kicker>AI Adoption</Kicker>}
    badge={<Chip>Current policy</Chip>}
    title="Evidence rules"
    sub="Meaningful use · sustained use · reviewed outcomes"
    footer={<div className="flex justify-end"><Action size="lg" variant="quiet" onClick={onClose}>Close</Action></div>}
  >
    <Tiles
      facts={[
        ...EVIDENCE_RULES,
        { label: 'Owner', value: `${managerLabel}: ${names(managers)}` },
        { label: 'Review cadence', value: 'Program review every 90 days' },
      ]}
    />
    <div className="mt-6">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">What happens</div>
      <div className="divide-y divide-line-soft border-t border-line-soft">
        {WHAT_HAPPENS.map((item) => (
          <div key={item.title} className="flex items-start gap-3.5 py-3.5">
            <span className="mt-0.5 grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full bg-[#3F7A2F] text-white"><Check size={14} strokeWidth={3} /></span>
            <div>
              <div className="text-[14px] font-semibold text-ink">{item.title}</div>
              <div className="mt-0.5 text-[12.5px] leading-[19px] text-ink-dim">{item.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </Drawer>
);

/* ── Compact readiness ──────────────────────────────────────────────────── */

const DepartmentTile: React.FC<{ dept: DepartmentAggregate }> = ({ dept }) => {
  const pct = dept.maturity?.maturity_pct ?? null;
  const suppressed = dept.suppressed || Boolean(dept.maturity?.suppressed);
  return (
    <div className="flex items-center justify-between gap-3 rounded-card border border-line px-4 py-3">
      <div className="min-w-0">
        <div className="truncate text-[13px] font-semibold text-ink">{dept.department || 'Unassigned'}</div>
        <div className="text-[12px] text-ink-dim">{suppressed ? dept.suppressed_reason || MIN_GROUP_HINT : `${dept.onboarded_count} onboarded`}</div>
      </div>
      <div className="shrink-0 text-right text-[14px] font-semibold tabular-nums text-ink">{maturityValue(pct, suppressed)}</div>
    </div>
  );
};

const ReadinessPanel: React.FC<{ dashboard: CompanyDashboard; trend: DashboardTrend | null; trendError: string | null }> = ({ dashboard, trend, trendError }) => {
  const company = dashboard.company;
  const suppressed = company.suppressed || Boolean(company.maturity?.suppressed);
  const departments = dashboard.departments || [];
  const ready = departments.filter((d) => !d.suppressed && (d.maturity?.maturity_pct ?? 0) >= 50).length;
  const points = trendPoints(trend);

  return (
    <Panel className="mb-5">
      <PanelHead kicker="Readiness" title="Company and team readiness" desc="Aggregates only, never individuals. Groups under four people read as protected." />
      <div className="grid gap-5 px-5 pb-5 md:px-6 lg:grid-cols-[1fr_1.4fr_1fr]">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
          <Stat tone="dark" label="Overall readiness" value={maturityValue(company.maturity?.maturity_pct, suppressed)} evidence={suppressed ? company.suppressed_reason || MIN_GROUP_HINT : `${company.onboarded_count} people`} />
          <Stat label="Onboarded" value={`${company.onboarded_count} / ${dashboard.enrolled}`} evidence={`${ready} of ${departments.length} functions at 50% or above`} />
        </div>
        <div>
          <Kicker className="mb-2">By function</Kicker>
          {departments.length === 0 && <EmptyState title="No departments yet" reason="Readiness by team appears once people are placed in departments and complete onboarding." />}
          <div className="grid gap-2 sm:grid-cols-2">
            {departments.map((d) => <DepartmentTile key={d.department} dept={d} />)}
          </div>
        </div>
        <div>
          <Kicker className="mb-2">Recent weeks</Kicker>
          {trendError && <ErrorNote message={trendError} />}
          {!trendError && points.length === 0 && <div className="py-6 text-center"><NotMeasured reason="Not enough history to show a trend" /></div>}
          {points.length > 0 && <MiniBarChart data={points} caption="Readiness by week" format={(v) => `${v}%`} />}
        </div>
      </div>
    </Panel>
  );
};

/* ── Page ───────────────────────────────────────────────────────────────── */

const OwnerAdoptionPage: React.FC = () => {
  const clientName = localStorage.getItem('client_name') || 'Your company';
  const { can } = usePermissions();
  const adoption = useAsync<OwnerAdoption>(() => aaiApi.getOwnerAdoption(), 'Could not load adoption controls.');
  const dashboard = useAsync<CompanyDashboard>(() => aaiApi.getDashboard(), 'Could not load company readiness.');
  const trend = useAsync<DashboardTrend>(() => aaiApi.getDashboardTrend(), 'Could not load the readiness trend.');
  const [downloading, setDownloading] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [exception, setException] = useState<SessionException | null>(null);
  const [proposal, setProposal] = useState<GraduationProposal | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await aaiApi.downloadReportPdf();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ai-adoption-report.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not prepare the report.');
    } finally {
      setDownloading(false);
    }
  };

  if (adoption.loading) return <PageBody><Loading label="Loading adoption controls" /></PageBody>;
  if (adoption.error || !adoption.data) return <PageBody><ErrorNote message={adoption.error || 'Something went wrong.'} /></PageBody>;

  const data = adoption.data;
  const program = data.program;
  const queueCount = (program ? 1 : 0) + data.exceptions.length + data.proposals.length;

  return (
    <PageBody>
      <PageHead
        align="center"
        title="Adoption controls"
        sub="Keep the program, evidence rules, and adoption delivery records current."
        actions={(
          <>
            <Action variant="quiet" icon={<Download size={14} />} onClick={downloadReport} disabled={downloading} reason="Preparing the report">Report</Action>
            <Action arrow to="/dashboard#results">Open Results</Action>
          </>
        )}
      />

      <div className="mb-5 grid overflow-hidden rounded-panel border border-line bg-white sm:grid-cols-3 sm:divide-x sm:divide-line">
        {[
          { label: 'Program review', value: data.counts.program_review, foot: data.counts.program_review > 0 ? 'Due' : 'Current' },
          { label: 'Delivery exceptions', value: data.counts.exceptions, foot: 'Open' },
          { label: 'Readiness reviews', value: data.counts.evidence_reviews, foot: 'Pending' },
        ].map((s) => (
          <div key={s.label} className="px-6 py-5">
            <div className="text-[12.5px] text-ink-dim">{s.label}</div>
            <div className="mt-1 text-[30px] font-bold leading-[36px] tabular-nums text-ink">{s.value}</div>
            <div className="mt-0.5 text-[12.5px] text-ink-dim">{s.foot}</div>
          </div>
        ))}
      </div>

      <div className="mb-5 grid gap-5 lg:grid-cols-[1.9fr_1fr]">
        <Panel>
          {!program && (
            <EmptyState title="No adoption program yet" reason="Your Solution Partner sets up the program. Its configuration, manager and reviews appear here once it exists." />
          )}
          {program && (
            <div className="px-6 py-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <Kicker>{data.review_due ? 'Review due' : 'Program current'}</Kicker>
                <Chip tone={data.review_due ? 'warn' : 'ok'}>{data.review_due ? 'Due' : 'Current'}</Chip>
              </div>
              <div className="mt-2 text-[26px] font-bold leading-[32px] tracking-[-0.015em] text-ink">{program.name} · {program.tier_label}</div>
              <p className="m-0 mt-2 text-[14px] leading-[22px] text-ink-muted">
                {data.review_due ? 'Configuration, manager, and next review need confirmation.' : `Confirmed ${relativeTime(data.program_review?.created_at)} by ${data.program_review?.actor_email || 'an Owner'}. Next review ${dateLabel(data.next_review, 'not scheduled')}.`}
                {!program.enabled && ' The program is paused.'}
              </p>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <div className="rounded-card bg-canvas-inset px-4 py-3.5">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Configuration</div>
                  <div className="mt-1 text-[13.5px] font-medium text-ink">{program.preset || 'Custom'} · {program.phases} phases</div>
                </div>
                <div className="rounded-card bg-canvas-inset px-4 py-3.5">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Manager</div>
                  <div className="mt-1 text-[13.5px] font-medium text-ink">{program.manager_label} · {names(data.managers)}</div>
                </div>
              </div>
              <Action className="mt-5" size="lg" onClick={() => setReviewOpen(true)}>Review program</Action>
            </div>
          )}
        </Panel>

        <div className="rounded-panel border border-line bg-canvas-inset px-6 py-6">
          <div className="flex items-start gap-4">
            <ShieldCheck size={22} className="mt-0.5 shrink-0 text-ink" />
            <div>
              <Kicker>Your view</Kicker>
              <div className="mt-1 text-[20px] font-bold leading-[26px] text-ink">Owner</div>
              <p className="m-0 mt-2 text-[13px] leading-[21px] text-ink-dim">
                Sponsors adoption and decides releases. Does not run the program. Employee practice remains private.
              </p>
            </div>
          </div>
        </div>
      </div>

      {dashboard.data && <ReadinessPanel dashboard={dashboard.data} trend={trend.data} trendError={trend.error} />}
      {dashboard.error && <ErrorNote message={dashboard.error} />}

      <Panel>
        <PanelHead kicker="Current work" title="Control queue" action={<span className="text-[12.5px] text-ink-dim">{queueCount} item{queueCount === 1 ? '' : 's'}</span>} />
        {queueCount === 0 && <EmptyState title="Nothing waiting" reason="Program reviews, delivery exceptions and evidence reviews appear here as they arise." />}
        <Divided>
          {program && (
            <ListRow
              mark={<RouteMark kind="adoption" size="md" />}
              title={program.name}
              desc={`Program configuration · ${program.manager_label}`}
              foot={data.review_due ? 'Configuration and next review due' : `Confirmed ${relativeTime(data.program_review?.created_at)}`}
              right={(
                <div className="flex items-center gap-3">
                  <Chip tone={data.review_due ? 'warn' : 'ok'}>{data.review_due ? 'Review due' : 'Current'}</Chip>
                  <Action size="sm" variant="quiet" onClick={() => setReviewOpen(true)}>Review program</Action>
                </div>
              )}
            />
          )}
          {data.exceptions.map((e) => (
            <ListRow
              key={e.id}
              mark={<RouteMark kind="adoption" size="md" />}
              title={e.title}
              desc={`Delivery exception · ${e.session_type}${e.cohort ? ` · ${e.cohort}` : ''}`}
              foot={e.request ? `Resolution requested ${relativeTime(e.request.created_at)}` : e.exception}
              right={(
                <div className="flex items-center gap-3">
                  <Chip tone={e.request ? 'neutral' : 'warn'}>{e.request ? 'Requested' : 'Open'}</Chip>
                  <Action size="sm" variant="quiet" onClick={() => setException(e)}>Resolve</Action>
                </div>
              )}
            />
          ))}
          {data.proposals.map((g) => (
            <ListRow
              key={g.id}
              mark={<RouteMark kind="quick_win" size="md" />}
              title={`Readiness evidence review · ${g.skill}`}
              desc={`${g.from_scope} to ${g.to_scope}${g.department ? ` · ${g.department}` : ''} · proposed by ${g.proposed_by || 'a participant'}`}
              foot={g.review ? `Reviewed ${relativeTime(g.review.created_at)}` : 'Confirm definitions and privacy threshold'}
              right={(
                <div className="flex items-center gap-3">
                  <Chip tone={g.review ? 'neutral' : 'warn'}>{g.review ? 'Reviewed' : 'Pending'}</Chip>
                  <Action size="sm" variant="quiet" onClick={() => setProposal(g)}>Review evidence</Action>
                </div>
              )}
            />
          ))}
        </Divided>
      </Panel>

      <div className="mt-4 flex justify-end">
        <Action variant="ghost" arrow onClick={() => setRulesOpen(true)}>Evidence policy</Action>
      </div>

      {reviewOpen && program && (
        <ProgramReviewDrawer data={data} companyName={clientName} canSwitch={can('aai_view_company_dashboard')} onClose={() => setReviewOpen(false)} onRecorded={adoption.reload} />
      )}
      {exception && <SessionExceptionDrawer item={exception} data={data} onClose={() => setException(null)} onRecorded={adoption.reload} />}
      {proposal && <EvidenceReviewDrawer item={proposal} onClose={() => setProposal(null)} onRecorded={adoption.reload} />}
      {rulesOpen && <EvidenceRulesDrawer managers={data.managers} managerLabel={program?.manager_label || 'Program manager'} onClose={() => setRulesOpen(false)} />}
    </PageBody>
  );
};

export default OwnerAdoptionPage;
